import {PanoramaModel} from "features/apartmentViewer/ApartmentViewer/models/PanoramaModel";
import {PanoramaLayer} from "features/apartmentViewer/ApartmentViewer/layers/PanoramaLayer";
import {MarkerModel} from "features/apartmentViewer/ApartmentViewer/models/MarkerModel";
import {PostProcessingRenderer} from "features/apartmentViewer/ApartmentViewer/renderers/PostProcessingRenderer";
import {CameraHandler} from "features/apartmentViewer/ApartmentViewer/services/CameraHandler";
import {MarkerLayer} from "features/apartmentViewer/ApartmentViewer/layers/MarkerLayer";
import {ApartmentViewerSettings} from "features/apartmentViewer/ApartmentViewer/ApartmentViewerSettings";

enum PanoramaTransitionState {
    Idle,
    Transitioning
}

export class PanoramaManager {

    private currentMarker: MarkerModel | undefined;

    private panoramaState = {
        currentIndex: 0,
        nextIndex: 1,
        currentAlpha: 0,
        nextAlpha: 0,
        transitionPercentage: 0,
        transitionMovement: 0,
        transitionState: PanoramaTransitionState.Idle,
    };

    constructor(
        private cameraHandler: CameraHandler,
        private postProcessingRenderer: PostProcessingRenderer,
        private markerLayer: MarkerLayer,
        private readonly panoramaModels: PanoramaModel[],
        private readonly panoramaLayers: PanoramaLayer[],
        private settings: ApartmentViewerSettings,
    ) {}

    async loadPanoramaData(marker: MarkerModel, panoramaIndex: number) {
        await this.panoramaModels[panoramaIndex].loadCamera(marker);
    }

    async moveToNewCamera(marker: MarkerModel, skipRotation: boolean = true) {
        this.startTransition();
        this.panoramaState.nextIndex = this.getNextIndex();
        await this.loadAndSetPanorama(marker, this.panoramaState.nextIndex, skipRotation);

        this.completeTransition();
    }

    async loadAndSetPanorama(marker: MarkerModel, index: number, skipRotation: boolean = true) {
        await this.loadPanoramaData(marker, index);
        this.panoramaLayers[index].setPanoramaData(this.panoramaModels[index]);
        this.cameraHandler.setCamera(marker.camera, skipRotation);
        this.currentMarker = marker;
    }

    async reloadCurrentPanorama() {
        if(!this.currentMarker) return;
        await this.loadAndSetPanorama(this.currentMarker, this.panoramaState.currentIndex);
    }

    update() {
        this.postProcessingRenderer.setPanoramaUniforms(
            this.panoramaModels[this.panoramaState.currentIndex].texture,
            this.panoramaModels[this.panoramaState.nextIndex].texture,
            this.panoramaState.transitionMovement,
            this.panoramaState.currentAlpha,
            this.panoramaState.nextAlpha,
            this.isInTransition() && this.panoramaState.transitionPercentage >= 0
        );
    }

    private startTransition() {
        this.panoramaState.transitionState = PanoramaTransitionState.Transitioning;
        this.panoramaState.transitionPercentage = -1;
        this.panoramaState.transitionMovement = 0;
    }

    private completeTransition() {
        this.panoramaState.currentIndex = this.panoramaState.nextIndex;
        this.panoramaState.transitionState = PanoramaTransitionState.Idle;
    }

    private getNextIndex(): number {
        return (this.panoramaState.currentIndex + 1) % this.panoramaModels.length;
    }

    public handleMarkerIfNotInTransition() {
        if (!this.isInTransition()) {
            this.handlePickedMarker();
        }
    }

    private async handlePickedMarker() {
        const pickedMarker = this.markerLayer.getPickedMarker();

        if(pickedMarker) {
            try {
                this.settings.loading = true;
                await this.moveToNewCamera(pickedMarker);
                this.markerLayer.setSelectedMarker(null);
            } catch (e) {
                console.error("Could not move to new camera:", e);
            } finally {
                this.settings.loading = false;
            }
        }
    }

    getCurrentPanorama(): PanoramaModel {
        return this.panoramaModels[this.panoramaState.currentIndex];
    }

    isInTransition(): boolean {
        return this.panoramaState.transitionState === PanoramaTransitionState.Transitioning;
    }

    dispose() {
        for (let index = 0; index < this.panoramaModels.length; index++) {
            this.panoramaLayers[index]?.dispose();
            this.panoramaModels[index]?.dispose();
        }
    }
}
